<template>
  <input type="hidden" :name="formElement.key" :value="inputValue" />
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { useCurrency, useDataAreaId, useGeolocation } from "@/core/composables";
import { globals } from "@/core/globals";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useUser } from "@/shared/account";
import { HiddenAutoCompleteTypes } from "../enums";
import type { ExtendedFormElement } from "../types";
import type { Product } from "@/core/api/graphql/types";

interface IProps {
  formElement: ExtendedFormElement;
  modelValue?: string;
  product?: Product;
  details?: string;
  familyPageId?: number;
  mailTo?: string;
  contactUsFormName?: string;
  orderNumber?: string;
}

const emit = defineEmits<{
  (event: "update:modelValue", value: string): void;
}>();

const props = defineProps<IProps>();
const inputValue = ref<string | number>();
const { getCountryCode } = useGeolocation();

onMounted(async () => {
  inputValue.value = await getValue();
});

watch(
  () => props.details,
  async () => {
    inputValue.value = await getValue();
  },
);

watch(inputValue, (val) => {
  emit("update:modelValue", String(val ?? ""));
});

async function getValue(): Promise<string | number | undefined> {
  switch (props.formElement.properties.autoCompleteValue) {
    case HiddenAutoCompleteTypes.Currency:
      return useCurrency().currentCurrency.value.code;
    case HiddenAutoCompleteTypes.CountryCode: {
      return await getCountryCode(globals.optimizelyUrl + "/");
    }
    case HiddenAutoCompleteTypes.DataAreaId: {
      const { dataAreaId } = useDataAreaId();
      return dataAreaId.value;
    }
    case HiddenAutoCompleteTypes.Details: {
      return props.details;
    }
    case HiddenAutoCompleteTypes.Email: {
      if (punchoutCookies.value) {
        return "";
      }
      const { user } = useUser();
      return user.value?.email;
    }
    case HiddenAutoCompleteTypes.FamilyPage:
      return props.familyPageId;
    case HiddenAutoCompleteTypes.ItemNumber:
      return props.product?.code;
    case HiddenAutoCompleteTypes.MailTo:
      return props.mailTo;
    case HiddenAutoCompleteTypes.Name: {
      const { user } = useUser();
      return user.value?.contact?.fullName;
    }
    case HiddenAutoCompleteTypes.Url:
      return window.location.href;
    case HiddenAutoCompleteTypes.UserId: {
      const { user } = useUser();
      return user.value?.id;
    }
    case HiddenAutoCompleteTypes.ContactUsFormName:
      return props.contactUsFormName;
    case HiddenAutoCompleteTypes.OrderNumber:
      return props.orderNumber;
    default:
      return "";
  }
}
</script>
